<template>
  <div class="d-flex flex-column">
    <Loading v-if="initialLoading" :type="3" />
    <div :style="initialLoading ? 'display: none !important' : ''">
      <GenericList
        :dataSet="servers"
        :binding="binding"
        :columnsMetaData="columnsMetaData"
        :prevPageRequest="prevPage"
        :nextPageRequest="nextPage"
        :noDataMessage="noDataMessage"
        :refreshData="{
          initialCountdownTime: countdownTime,
          shouldStartInterval: requestDone
        }"
        @paginateClicked="refreshServerStatus"
        @onTimeoutCompleted="refreshServerStatus"
      >
        <div slot="header">
          <h4 class="d-inline">
            {{ "sections.listServersSection.title" | i18n }}
          </h4>
          <router-link
            v-if="this.clientId != null"
            class="float-right text-decoration-none d-inline"
            :to="'/clients/' + this.clientId + '/servers'"
            ><i class="external alternate icon text-primary"
          /></router-link>
        </div>
        <GenericPaginationSubHeader
          slot="subheader"
          :from="from"
          :to="to"
          :total="totalServers"
          :text="$i18n.t('sections.listServersSection.servers')"
        />
      </GenericList>
      <div
        v-if="clientId != null"
        :style="initialLoading ? 'display: none !important' : ''"
      >
        <GenericBtnAdd @click="addServer" />
        <FormServerSection
          :isShown="show"
          :clientId="'' + clientId"
          v-on:canceled="close"
          @successed-request="close"
          id="FormServerSection"
        />
      </div>
    </div>
  </div>
</template>
<script>
import {
  Loading,
  GenericPaginationSubHeader,
  StateCircleColor,
  GenericList,
  GenericBtnAdd
} from "@/components";
import FormServerSection from "../forms/FormServerSection.vue";
/**
 * @description Affiche la liste des statuts des logiciels d'un serveur
 * @author Samuel Colassin
 * @author Gabriel Bertrand
 * @author Kevin Dubé
 * @author Alexandre Gallant
 * @author Félix Bordage
 * @vue-prop {Number} clientId - L'id du client
 * @vue-data {Boolean} show - Définit si le modal est affiché ou non
 * @vue-data {Array} servers - La liste de tous les serveurs
 * @vue-data {Number} currentPage - La page courante dans la pagination
 * @vue-data {Number} totalServers - Le nombre total de serveurs
 * @vue-data {Number} countdownTime - L'intervalle de temps qu'on désire rafraichir les données
 * @vue-data {Number} currentTime - Le temps avant la prochaine mise à jour des données (en secondes)
 * @vue-data {Boolean} requestDone - Si la requête http est terminée pour indiquer
 * qu'on peut démarrer un nouveau décompte
 * @vue-data {Number} from - Le n ième logiciel au début de la page courante
 * @vue-data {Number} to - Le n ième logiciel à la fin de la page courante
 * @vue-data {String} nextPage - L'url de la prochaine page dans la pagination
 * @vue-data {String} prevPage - L'url de la précédente page dans la pagination
 * @vue-data {Boolean} initialLoading - Si le composant est en état de chargement initial
 * @vue-data {String} noDataMessage - Le message à afficher lorsqu'il n'y a pas de données
 * @vue-data {Object} binding - Les données de liaison
 * @vue-data {Array} columnsMetaData - Les données des colonnes
 */
export default {
  components: {
    Loading,
    GenericList,
    GenericPaginationSubHeader,
    FormServerSection,
    GenericBtnAdd
  },
  name: "ListServersSection",
  props: {
    clientId: {
      type: Number,
      required: false,
      default: null
    }
  },
  data() {
    return {
      show: false,
      servers: [],
      currentPage: 1,
      totalServers: 0,
      countdownTime: 60000, // millisecondes
      currentTime: 60,
      requestDone: true,
      from: 0,
      to: 0,
      nextPage: "",
      prevPage: "",
      initialLoading: true,
      noDataMessage: this.$i18n.t("sections.GenericList.serverNoData"),
      binding: {
        etat: {
          component: {
            type: StateCircleColor,
            props: p => {
              return {
                state: p.etat
              };
            }
          }
        },
        nom: {},
        ip: {},
        ...((this.clientId && {
          moyenne_CPU: {}
        }) || {
          client_nom: {},
          details: {
            component: {
              type: "router-link",
              props: s => {
                return {
                  to: `/clients/${s.client_id}/servers`
                };
              }
            },
            transform: () => "Détails"
          }
        })
      },
      columnsMetaData: [
        {
          name: this.$i18n.t("sections.listServersSection.headerStatus"),
          class: "col-2 text-break"
        },
        {
          name: this.$i18n.t("sections.listServersSection.headerName"),
          class: "col-8 col-md-4 text-break"
        },
        {
          name: this.$i18n.t("sections.listServersSection.headerIP"),
          class: "col-3 d-none d-md-block text-break"
        },
        ...((this.clientId && [
          {
            name: this.$i18n.t("sections.listServersSection.headerCPU"),
            class: "d-none col-md-2 d-md-block"
          }
        ]) || [
          {
            name: this.$i18n.t("sections.listServersSection.headerClientName"),
            class: "d-none d-md-block col-md-2 text-break"
          },
          {
            name: this.$i18n.t("sections.listServersSection.headerLink"),
            class: "col-2 col-md-1"
          }
        ])
      ]
    };
  },
  /**
   * @description Effectue une première requête pour injecter l'information dans
   * le component et ajuste la/les dernières colonnes, selon le contexte
   */
  async beforeMount() {
    this.alignLastColumnToRight();
    await this.refreshServerStatus();
  },
  methods: {
    /**
     * @description formatte de façon universelle la dernière ligne
     */
    alignLastColumnToRight() {
      this.columnsMetaData[this.columnsMetaData.length - 1].class +=
        " text-right";
    },
    /**
     * @description Permet de fermer le modal
     */
    close() {
      this.show = false;
    },
    /**
     * @description Permet d'ouvrir le modal
     */
    addServer() {
      this.show = true;
    },
    /**
     * @description Fait une requête pour mettre à jour le statut des logiciels
     * @param {String} pageUrl L'url de la requête de la page qu'on désire dans la pagination
     */
    async refreshServerStatus(pageUrl = "") {
      if (!this.requestDone) {
        return;
      }
      // On ne veut pas démarrer un nouveau décompte si on change de page
      if (pageUrl == "") {
        this.requestDone = false;
      }
      await (this.clientId == null
        ? this.$api.getServers(pageUrl)
        : this.$api.getServersByClient(this.clientId, pageUrl)
      )
        .then(res => {
          this.currentPage = res.data.meta.current_page;
          this.to = res.data.meta.to || 0;
          this.from = res.data.meta.from || 0;
          this.nextPage = res.data.links.next || "";
          this.prevPage = res.data.links.prev || "";
          this.servers = res.data.data;
          this.totalServers = res.data.meta.total;
        })
        .catch(() => {})
        .finally(() => {
          this.requestDone = true;
          this.initialLoading = false;
        });
    },
    /**
     * @description Met à jour le temps restant avant la prochaine mise à jour des données
     * @param {Number} time Le temps restant au compteur
     */
    updateCountdown(time) {
      this.currentTime = time / 1000;
    }
  }
};
</script>
